<script lang="ts">
    import * as Table from '@/components/ui/table/index.js';
    import * as Card from '@/components/ui/card';
    import Th from './th.svelte';
    import type { Column, Props } from './index.js';
    import { onMount } from 'svelte';
    import CellRender from './cellRender.svelte';
    import Paginator from './paginator.svelte';
    import {LoaderCircle, Save, FilterX} from 'lucide-svelte';
    import Debug from '../Debug.svelte';
    import ThFilter from './thFilter.svelte';
    import Checkbox from '../Inputs/Checkbox.svelte';
    import {Button} from "@/components/ui/button";

    const rowCount = [
        { value: 10, label: '10' },
        { value: 25, label: '25' },
        { value: 50, label: '50' },
        { value: 100, label: '100' },
    ];

    let {
        columns = $bindable(),
        sorter = $bindable(null),
        isLoading = $bindable(false),
        limit = $bindable(50),
        offset = $bindable(0),
        debounceMs = 350,
        fetchOnMount = true,
        rowCountOptions = rowCount,
        refetch = $bindable(),
        fetcher,
        data = $bindable(null),
        error = $bindable(null),
        onInput,
        onAfterFetch,
        aSnippet,
        onRowClick = $bindable(() => {}),
        selected = $bindable(),
        getId = (row) => row?.id,
        onSubmitClick = $bindable(() => {}),
        entityColumn = null,
        title = null,
        description = null,
        headerSnippet,
    }: Props = $props();


    const getFilters = (columns: Column[]) => {
        const filters: Record<string, any> = {};
        for (const col of columns) {
            if (col == null) continue;
            if (col.filter?.value != null) {
                filters[col.key] = col.filter.value;
            }
        }
        return filters;
    };
    const fetchData = async (preserveScroll = false) => {
        const scrollY = preserveScroll ? window.scrollY : null;
        isLoading = true;
        try {
            const res = await fetcher({
                sorter,
                columns,
                limit,
                offset,
                filters: getFilters(columns),
            });
            data = res.data;
            error = res.error;
        } catch (e) {
            console.error(e);
        }
        isLoading = false;
        if (scrollY !== null) {
            // Use tick to wait for DOM update, then restore scroll
            requestAnimationFrame(() => window.scrollTo(0, scrollY));
        }
        return;
    };
    refetch = () => fetchData(true);

    let fetchTimeout = 0 as unknown as ReturnType<typeof setTimeout>;
    const fetchInternal = async () => {
        if (onInput) onInput();
        await fetchData();
        if (onAfterFetch) onAfterFetch();
    };
    const onInputInternal = async () => {
        clearTimeout(fetchTimeout);
        fetchTimeout = setTimeout(fetchInternal, debounceMs);
    };

    let currentlyHovering: null | { href: string; rowKey: unknown } = $state(null);

    // Check if any filters have values
    const hasActiveFilters = $derived(
        columns.some((col) => col?.filter?.value != null && col.filter.value !== '')
    );

    // Clear all filters
    const clearFilters = () => {
        for (const col of columns) {
            if (col?.filter) {
                col.filter.value = undefined;
            }
        }
        offset = 0;
        fetchInternal();
    };

    onMount(() => {
        if (fetchOnMount) {
            fetchInternal();
        }
        clearTimeout(fetchTimeout);
    });
</script>

<!-- <Debug {currentlyHovering}></Debug> -->
<Card.Root>
    {#if title || headerSnippet || hasActiveFilters}
        <Card.Header>
            <div class="flex items-center justify-between">
                <div>
                    {#if title}
                        <Card.Title>{title}</Card.Title>
                    {/if}
                    {#if description}
                        <Card.Description>{description}</Card.Description>
                    {/if}
                </div>
                <div class="flex items-center gap-2">
                    {#if hasActiveFilters}
                        <Button variant="outline" size="sm" onclick={clearFilters}>
                            <FilterX class="mr-2 h-4 w-4" />
                            Clear Filters
                        </Button>
                    {/if}
                    {#if headerSnippet}
                        {@render headerSnippet()}
                    {/if}
                </div>
            </div>
        </Card.Header>
    {/if}
    <Card.Content class="p-0">
        <div class="flex w-full flex-col">
            <Table.Root>
                 <Table.Caption>A list of referrals.</Table.Caption>
                <Table.Header>
                    <Table.Row class="border-none">
                        {#if selected != null}
                            <Table.Head class="text-center"></Table.Head>
                        {/if}
                        {#each columns as column, i}
                            {#if column != null}
                                <Th
                                    bind:column={columns[i]}
                                    {sorter}
                                    sortCol={() => {
                                        if (sorter?.key != column.key) {
                                            sorter = {
                                                key: column.key,
                                                dir: 'asc',
                                            };
                                        } else if (sorter.dir === 'asc') {
                                            sorter.dir = 'desc';
                                        } else {
                                            sorter = null;
                                        }
                                        offset = 0;
                                        fetchData();
                                    }}
                                ></Th>
                            {/if}
                        {/each}
                        <!-- <Table.Head class="text-center">Actions</Table.Head> -->
                    </Table.Row>
                    <Table.Row>
                        {#if selected != null}
                            <Table.Head class="text-center pb-2">{selected.size} rows <br/> selected</Table.Head>
                        {/if}
                        {#each columns as column, i}
                            {#if column != null}
                                <ThFilter
                                    bind:column={columns[i]}
                                    setFilter={(filter) => {
                                        column.filter.value = filter;
                                        offset = 0;
                                        onInputInternal();
                                    }}
                                    {sorter}
                                    sortCol={() => {
                                        if (sorter?.key != column.key) {
                                            sorter = {
                                                key: column.key,
                                                dir: 'asc',
                                            };
                                        } else if (sorter.dir === 'asc') {
                                            sorter.dir = 'desc';
                                        } else {
                                            sorter = null;
                                        }
                                        offset = 0;
                                        fetchData();
                                    }}
                                />
                            {/if}
                        {/each}
                        <!-- <Table.Head class="text-center">Actions</Table.Head> -->
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {#if isLoading}
                        <tr>
                            <td colspan={9999}>
                                <div class="flex h-full w-full items-center justify-center p-8">
                                    <LoaderCircle class="animate-spin"></LoaderCircle>
                                </div>
                            </td>
                        </tr>
                    {:else if data}
                        {#if data.count === 0}
                            <tr>
                                <td colspan={9999} class="p-4 text-center"> No records found. </td>
                            </tr>
                        {/if}
                        {#each data.rows as row, i (i)}
                            {@const isSelected = selected ? selected.has(getId(row)) : false}
                            <!--                    <Debug {row}></Debug>-->
                            <Table.Row
                                class={isSelected
                                        ? 'bg-primary/25'
                                        : (i % 2) - 1
                                          ? 'bg-muted/25'
                                          : ''}
                            >
                                {#if selected}
                                    <Table.Cell>
                                        <Checkbox
                                            bind:checked={
                                                    () => !!selected.get(getId(row)),
                                                    (v) => v ? selected.set(getId(row), row[entityColumn ?? "id"]) : selected.delete(getId(row))
                                                }
                                            class="mx-4"
                                        />
                                    </Table.Cell>
                                {/if}
                                {#each columns as column}
                                    {#if column != null}
                                        <CellRender
                                            {column}
                                            {row}
                                            bind:currentlyHovering
                                            {aSnippet}
                                            {onRowClick}
                                        ></CellRender>
                                    {/if}
                                {/each}
                            </Table.Row>
                        {/each}
                    {/if}
                </Table.Body>
            </Table.Root>

            <Paginator bind:offset bind:limit {data} {fetchData} {rowCountOptions}>
                {#if selected}
                    <Button
                        onclick={onSubmitClick}
                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground">
                        <Save /> Save Selected
                    </Button>
                {/if}
            </Paginator>

        </div>
    </Card.Content>
</Card.Root>
